// #667 Phase 3 of #664: サービスが使えない状態の時に表示する全画面 page。
//
// 客が「カード入力 → 決済ボタン → 「処理中…」のまま hang」のような silent failure を
// 見せず、明示的に「今は使えない、後でお試しください」と伝える UX。MaintenanceGuard が
// /api/v1/health/full を polling し、healthy=false / 取得失敗で本 page を表示する。
//
// **原因によって文言を分ける。** お金を扱う画面で「決済システムに障害」と出すのは
// 重い表現で、実際には客の電波が切れているだけ (network_error) のケースが大半だった。
// - connection: health を取りに行けなかった (オフライン / サーバに到達できない)
// - server:     API が 4xx/5xx を返した (決済に限らずサービス全体の問題)
// - payment:    API は生きていて、Stripe 監視が degraded を返した (stripe_* /
//               payment_failure_burst)。決済を止めている、と正確に伝える
import { AlertTriangle, RefreshCw, WifiOff, Wrench } from 'lucide-react'
import { useI18n } from '../i18n/useI18n'
import type { MaintenanceNotice } from '../lib/maintenanceNotice'

// - maintenance: #2692 意図して止めた (Maintenance の workflow)。文言は Firestore の
//               `platform_config/maintenance` から (題名・本文・いつまで)。障害の文言とは分ける
type Kind = 'connection' | 'server' | 'payment' | 'maintenance'

function kindOf(reason: string | null | undefined): Kind {
  if (reason === 'network_error') return 'connection'
  if (reason?.startsWith('http_')) return 'server'
  return 'payment'
}

interface Props {
  reason?: string | null
  onRetry: () => void
  checking?: boolean
  /** #2692 意図して止めたときの文言 (無ければ reason から決まる既定の文言) */
  notice?: MaintenanceNotice | null
}

export default function MaintenancePage({ reason, onRetry, checking = false, notice = null }: Props) {
  const { t, locale } = useI18n()
  const kind: Kind = notice ? 'maintenance' : kindOf(reason)
  const Icon = kind === 'connection' ? WifiOff : kind === 'maintenance' ? Wrench : AlertTriangle
  // 文言は ja と en だけ持つ。日本語以外のロケールは en (無ければ ja)
  const pick = (m: { ja?: string; en?: string }) => (locale === 'ja' ? m.ja || m.en : m.en || m.ja) || ''
  const title = notice ? pick(notice.title) || t('maintenance.server.title') : t(`maintenance.${kind}.title`)
  const message = notice ? pick(notice.message) || t('maintenance.server.message') : t(`maintenance.${kind}.message`)
  const until = notice ? pick(notice.until) : ''
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 p-6 text-center">
      <Icon
        className={`h-16 w-16 ${kind === 'connection' ? 'text-gray-400' : kind === 'maintenance' ? 'text-navy' : 'text-yokoso-red'}`}
        strokeWidth={1.5}
      />
      <h1 className="mt-6 text-xl font-bold text-navy">
        {title}
      </h1>
      <p className="mt-3 text-sm leading-relaxed text-gray-600 whitespace-pre-line">
        {message}
      </p>
      {until && (
        <p className="mt-2 text-sm font-medium text-navy">
          {t('maintenance.notice.until', { until })}
        </p>
      )}
      <button
        onClick={onRetry}
        disabled={checking}
        className="tap-effect mt-8 flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-medium text-white disabled:opacity-60"
      >
        <RefreshCw className={`h-4 w-4 ${checking ? 'animate-spin' : ''}`} />
        {t('maintenance.retry')}
      </button>
      {reason && kind !== 'maintenance' && (
        // dev / ops 向けの hint。client 側で reason を見せて初動を早める。
        // 客には意味不明だが平塚バー店員 / 自分が現場で「あ、stripe 系だな」と即判断できる。
        <p className="mt-6 text-[10px] text-gray-400">
          ref: {reason}
        </p>
      )}
    </div>
  )
}
